/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
/* eslint-disable max-len */
import Numberflow from '@number-flow/react';
import { useSpring, animated, config } from '@react-spring/web';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect } from 'react';

import { Portal } from './Portal';

interface ImageCarouselProps {
    images: { url: string }[];
    currentIndex: number;
    onClose: () => void;
    onNavigate: (index: number) => void;
}

export const ImageCarousel = ({ images, currentIndex, onClose, onNavigate }: ImageCarouselProps) => {
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowLeft') onNavigate(Math.max(0, currentIndex - 1));
            if (e.key === 'ArrowRight') onNavigate(Math.min(images.length - 1, currentIndex + 1));
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentIndex, onClose, onNavigate, images.length]);

    const fadeIn = useSpring({
        from: { opacity: 0 },
        to: { opacity: 1 },
        config: config.gentle,
    });

    const controls = useSpring({
        from: { opacity: 0, y: 20 },
        to: { opacity: 1, y: 0 },
        delay: 200,
        config: config.gentle,
    });

    const leftButton = useSpring({
        from: { opacity: 0, x: -20 },
        to: { opacity: 1, x: 0 },
        delay: 200,
        config: config.gentle,
    });

    const rightButton = useSpring({
        from: { opacity: 0, x: 20 },
        to: { opacity: 1, x: 0 },
        delay: 200,
        config: config.gentle,
    });

    const slideAnimation = useSpring({
        x: currentIndex * -100,
        config: { tension: 280, friction: 30 },
    });

    return (
        <Portal>
            <animated.div
                style={fadeIn}
                className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center backdrop-blur-lg backdrop-saturate-[180%]"
                onClick={onClose}
            >
                <animated.button
                    style={controls}
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2 text-white bg-purple-900/10 hover:bg-purple-600/20 z-[1] backdrop-blur-lg rounded-full"
                >
                    <X className="h-6 w-6" />
                </animated.button>

                <animated.button
                    style={leftButton}
                    onClick={(e) => {
                        e.stopPropagation();
                        onNavigate(Math.max(0, currentIndex - 1));
                    }}
                    className="absolute left-4 p-2 text-white bg-purple-900/10 hover:bg-purple-600/20 z-[1] backdrop-blur-lg rounded-full"
                    disabled={currentIndex === 0}
                >
                    <ChevronLeft className="h-6 w-6" />
                </animated.button>

                <div className="relative overflow-hidden w-screen h-[90vh] pointer-events-none">
                    <animated.div
                        style={{
                            display: 'flex',
                            width: `${images.length * 100}%`,
                            transform: slideAnimation.x.to((x) => `translateX(${x / images.length}%)`),
                        }}
                        className="h-full"
                    >
                        {images.map((image, idx) => (
                            <div key={idx} className="w-full h-full flex items-center justify-center">
                                <img
                                    onClick={(e) => e.stopPropagation()}
                                    src={image.url}
                                    alt={`Screenshot ${idx + 1}`}
                                    className="max-h-full max-w-full object-contain pointer-events-auto"
                                />
                            </div>
                        ))}
                    </animated.div>
                </div>

                <animated.button
                    style={rightButton}
                    onClick={(e) => {
                        e.stopPropagation();
                        onNavigate(Math.min(images.length - 1, currentIndex + 1));
                    }}
                    className="absolute right-4 p-2 text-white bg-purple-900/10 hover:bg-purple-600/20 z-[1] backdrop-blur-lg rounded-full"
                    disabled={currentIndex === images.length - 1}
                >
                    <ChevronRight className="h-6 w-6" />
                </animated.button>

                <animated.div
                    style={controls}
                    className="absolute bottom-4 z-[1] p-2 text-white bg-purple-900/10 z-[1] backdrop-blur-lg rounded-full [text-box:trim-both]"
                >
                    <Numberflow value={currentIndex + 1} suffix={` / ${images.length}`} />
                </animated.div>
            </animated.div>
        </Portal>
    );
};
